import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown, Table2 } from 'lucide-react';
import type { ReportColumn, ReportValueFormat } from '../../../../types/reports';
import { formatCell } from '../reportFormat';
import { StatusBadge } from './StatusBadge';

function compareValues(a: unknown, b: unknown, format?: ReportValueFormat): number {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (format === 'date' || format === 'datetime') {
    const aTime = new Date(String(a)).getTime();
    const bTime = new Date(String(b)).getTime();
    if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) return aTime - bTime;
  }

  if (format === 'number' || format === 'currency' || format === 'percent') {
    const aNum = Number(a);
    const bNum = Number(b);
    if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function DetailsTable({
  columns,
  rows,
  timezone,
}: {
  columns: ReportColumn[];
  rows: Record<string, unknown>[];
  timezone?: string;
}) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const column = columns.find((item) => item.key === sortKey);
    return [...rows].sort((a, b) => {
      const result = compareValues(a[sortKey], b[sortKey], column?.format);
      return sortDir === 'asc' ? result : -result;
    });
  }, [rows, columns, sortKey, sortDir]);

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('asc');
  };

  if (rows.length === 0) {
    return (
      <div className="px-4 py-12 text-center text-text-muted">
        <Table2 size={32} className="mx-auto mb-3 opacity-40" />
        No records found for the selected filters.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-background/50">
            {columns.map((column) => {
              const isSorted = sortKey === column.key;
              return (
                <th
                  key={column.key}
                  className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase whitespace-nowrap"
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(column.key)}
                    className={`inline-flex items-center gap-1 hover:text-text transition-colors ${
                      isSorted ? 'text-text' : ''
                    }`}
                    title={`Sort by ${column.label}`}
                  >
                    {column.label}
                    {isSorted ? (
                      sortDir === 'asc' ? (
                        <ArrowUp size={13} />
                      ) : (
                        <ArrowDown size={13} />
                      )
                    ) : (
                      <ChevronsUpDown size={13} className="opacity-40" />
                    )}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sortedRows.map((row, index) => (
            <tr key={index} className="hover:bg-background/50 transition-colors">
              {columns.map((column) => (
                <td key={column.key} className="px-4 py-3 text-sm text-text whitespace-nowrap">
                  {column.format === 'status' ? (
                    <StatusBadge value={row[column.key]} />
                  ) : (
                    formatCell(row[column.key], column.format, timezone)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
